import { useQuery } from '@tanstack/react-query';
import { useIsFocused } from 'expo-router/react-navigation';

import { useAskUiStore } from '@/components/libraryChat/LibraryChatContext';
import { THREE_SECONDS } from '@/constants/time';
import { getAskConversationV2QueryOptions } from '@/services/ask/queryOptions';
import { hasActiveTurn } from '@/services/ask/selectors';
import { selectIsLive } from '@/services/stores/askUiStore';

/** How often the snapshot is re-read while a turn runs and the socket can't announce its end. */
export const ASK_POLL_INTERVAL = THREE_SECONDS;

/**
 * The open conversation's snapshot.
 *
 * Polls only while a turn is active, the socket is not subscribed, and the
 * screen is in view: that is the one window in which nothing else would tell
 * the reader the turn ended. A screen left mounted beneath another one stops
 * polling, and is re-read when it comes back into view. The interval switches
 * itself off as soon as any of the three stops being true.
 *
 * A new conversation has nothing on the server until its first turn is
 * accepted, so it reads only what the submit seeded and never fetches, which
 * would otherwise come back `404` and close the screen.
 */
export function useAskConversation(conversationId: string, { isNew }: { isNew: boolean }) {
  const isLive = useAskUiStore(selectIsLive);

  const isFocused = useIsFocused();

  return useQuery({
    ...getAskConversationV2QueryOptions(conversationId),
    enabled: !isNew,
    refetchInterval: (query) =>
      isFocused && !isLive && hasActiveTurn(query.state.data) ? ASK_POLL_INTERVAL : false,
  });
}
